import { Injectable } from '@angular/core';
import {
  getFirestore,
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  getDocs,
  arrayUnion,
  arrayRemove,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { User } from '../models/user.model';
import { ROLE_STUDENT, ROLE_ADMIN, COLLECTION_USERS } from '../global';
import { from } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class UserService {

  getUser(id: string) {
    return from(getDoc(doc(getFirestore(), COLLECTION_USERS, id)));
  }

  addPatientIdsToUser(patientIds: string[], userId: string) {
    const db = getFirestore();
    return from(updateDoc(doc(db, COLLECTION_USERS, userId), { patientIds: arrayUnion(...patientIds) }));
  }

  removePatientIdsFromUser(patientIds: string[], userId: string) {
    const db = getFirestore();
    return from(updateDoc(doc(db, COLLECTION_USERS, userId), { patientIds: arrayRemove(...patientIds) }));
  }

  setPatientIdsForUser(patientIds: string[], userId: string) {
    const db = getFirestore();
    return from(setDoc(doc(db, COLLECTION_USERS, userId), { patientIds }, { merge: true }));
  }

  deletePatientIdFromAllUsers(id: string) {
    const db = getFirestore();
    const q = query(collection(db, COLLECTION_USERS), where('patientIds', 'array-contains', id));
    return from(
      getDocs(q).then((snapshot) =>
        Promise.all(snapshot.docs.map((d) => updateDoc(d.ref, { patientIds: arrayRemove(id) })))
      )
    );
  }

  getAllStudents() {
    const db = getFirestore();
    const q = query(collection(db, COLLECTION_USERS), where('role', '==', ROLE_STUDENT));
    return from(getDocs(q));
  }

  async getAllAdministrators(): Promise<User[]> {
    const db = getFirestore();
    const q = query(collection(db, COLLECTION_USERS), where('role', '==', ROLE_ADMIN));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => {
      const data = d.data();
      const user = new User(data['email'], data['firstName'], data['lastName'], data['role']);
      user.id = d.id;
      user.patientIds = data['patientIds'] || [];
      user.studentIds = data['studentIds'] || [];
      return user;
    });
  }

  async addPatientToAdministrator(adminId: string, patientId: string): Promise<void> {
    const db = getFirestore();
    await updateDoc(doc(db, COLLECTION_USERS, adminId), { patientIds: arrayUnion(patientId) });
  }

  getCurrentUserId(): string | undefined {
    return getAuth().currentUser?.uid;
  }

  async createUserAccount(user: User) {
    const createUser = httpsCallable(getFunctions(), 'createUserV2');
    const result = await createUser({ user });
    user.id = (result.data as any).uid;
    return user;
  }

  upsertUserDocument(user: User) {
    const db = getFirestore();
    const obj = this.extractUserData(user);
    return setDoc(doc(db, COLLECTION_USERS, user.id), obj);
  }

  deleteStudentIDFromAllUsers(id: string) {
    const db = getFirestore();
    const q = query(collection(db, COLLECTION_USERS), where('studentIds', 'array-contains', id));
    getDocs(q).then((snapshot) =>
      Promise.all(snapshot.docs.map((d) => updateDoc(d.ref, { studentIds: arrayRemove(id) })))
    );
  }

  deleteUser(id: string, col: string) {
    const db = getFirestore();
    deleteDoc(doc(db, col, id));
    const deleteUser = httpsCallable(getFunctions(), 'deleteUserV2');
    return deleteUser({ id });
  }

  private extractUserData(user: User): object {
    const obj = JSON.parse(JSON.stringify(Object.assign({}, user)));
    delete obj.id;
    return obj;
  }
}
